import React, { useState, useEffect } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, ResponsiveContainer, Legend,
} from 'recharts';
import Icon from '../../../components/AppIcon';
import { EmptyState } from './shared';

const BLUE        = '#004AAD';
const GRAD_ACTIVE = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';

const CompetitorBenchmark = ({ categoryScores, benchmarkData, competitorData }) => {
  const [benchView, setBenchView] = useState('radar');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const iltcNum      = parseInt(benchmarkData?.yourScore) || 0;
  const industryAvg  = parseInt(benchmarkData?.industry)  || 0;
  const topPerformer = parseInt(benchmarkData?.topDecile) || 0;

  const radarData = categoryScores.map(c => ({
    subject: c.name,
    deck: parseInt(String(c.score).match(/\d+/)?.[0] || 0),
    avg:  industryAvg   || undefined,
    top:  topPerformer  || undefined,
  }));

  return (
    <div style={{ fontFamily: 'inherit', width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>

      {/* ── Page header ── */}
      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? 16 : (isTablet ? 20 : 24),
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 10 : 0,
      }}>
        <h2 style={{
          fontFamily: "'Sansation', sans-serif",
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: '#0f172a', 
          margin: 0,
        }}>
          Competitor Benchmark
        </h2>

        {/* ── View toggle ── */}
        <div style={{ 
          display: 'flex', 
          border: '1px solid #e2e8f0', 
          borderRadius: isMobile ? 8 : 10, 
          overflow: 'hidden', 
          background: '#f8fafc',
          width: isMobile ? '100%' : 'auto',
        }}>
          {[
            { k: 'radar', l: 'Radar', ic: 'Target' },
            { k: 'table', l: 'Table', ic: 'Table2' },
          ].map((b, bi) => {
            const isActive = benchView === b.k;
            return (
              <button
                key={b.k}
                onClick={() => setBenchView(b.k)}
                style={{
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: isMobile ? 4 : 6,
                  padding: isMobile ? '6px 12px' : '7px 16px',
                  fontFamily: 'inherit',
                  fontSize: isMobile ? 11 : 12, 
                  fontWeight: 600,
                  cursor: 'pointer', 
                  transition: 'all 0.15s',
                  borderRight: bi === 0 ? '1px solid #e2e8f0' : 'none',
                  border: 'none',
                  background: isActive ? GRAD_ACTIVE : 'transparent',
                  color:      isActive ? '#ffffff'   : '#64748b',
                  flex: isMobile ? 1 : 'none',
                  justifyContent: isMobile ? 'center' : 'flex-start',
                }}
              >
                <Icon name={b.ic} size={isMobile ? 12 : 13} color={isActive ? '#fff' : '#64748b'} />
                {b.l}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RADAR VIEW ── */}
      {benchView === 'radar' && (
        radarData.length > 0 ? (
          <div style={{ 
            background: '#ffffff', 
            border: '0.5px solid rgba(0,0,0,0.06)', 
            borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            <div style={{ 
              padding: isMobile ? '20px 12px 8px' : (isTablet ? '24px 18px 8px' : '28px 24px 8px'), 
              height: isMobile ? 280 : (isTablet ? 320 : 340) 
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart 
                  data={radarData} 
                  margin={
                    isMobile 
                      ? { top: 10, right: 20, bottom: 10, left: 20 }
                      : isTablet
                      ? { top: 15, right: 30, bottom: 15, left: 30 }
                      : { top: 20, right: 40, bottom: 20, left: 40 }
                  }
                >
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis 
                    dataKey="subject" 
                    tick={{ 
                      fill: '#94a3b8', 
                      fontSize: isMobile ? 10 : 12 
                    }} 
                  />
                  <PolarRadiusAxis 
                    angle={90} 
                    domain={[0, 100]} 
                    tick={{ 
                      fill: '#94a3b8', 
                      fontSize: isMobile ? 9 : 10 
                    }} 
                    tickCount={5} 
                  />
                  <Radar 
                    name="Your Deck" 
                    dataKey="deck" 
                    stroke={BLUE} 
                    fill={BLUE} 
                    fillOpacity={0.15} 
                    strokeWidth={isMobile ? 1.5 : 2} 
                  />
                  {industryAvg > 0 && (
                    <Radar 
                      name="Industry Avg" 
                      dataKey="avg" 
                      stroke="#14b8a6" 
                      fill="#14b8a6" 
                      fillOpacity={0.10} 
                      strokeWidth={isMobile ? 1.5 : 2} 
                    />
                  )}
                  {topPerformer > 0 && (
                    <Radar 
                      name="Top Performer" 
                      dataKey="top" 
                      stroke="#16a34a" 
                      fill="#16a34a" 
                      fillOpacity={0.08} 
                      strokeWidth={isMobile ? 1.5 : 2} 
                    />
                  )}
                  <Legend 
                    formatter={v => (
                      <span style={{ 
                        fontFamily: 'inherit', 
                        fontSize: isMobile ? 10 : 12, 
                        color: '#64748b' 
                      }}>
                        {v}
                      </span>
                    )} 
                    wrapperStyle={
                      isMobile 
                        ? { fontSize: 10, paddingTop: 8 } 
                        : {}
                    }
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Score summary row */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile 
                ? '1fr' 
                : 'repeat(auto-fit, minmax(140px, 1fr))', 
              borderTop: '0.5px solid rgba(0,0,0,0.06)' 
            }}>
              {[
                iltcNum      > 0 && { label: 'Your Deck',        score: `${iltcNum}/100`,      color: BLUE,      border: BLUE      },
                industryAvg  > 0 && { label: 'Industry Average', score: `${industryAvg}/100`,  color: '#0d9488', border: '#0d9488' },
                topPerformer > 0 && { label: 'Top Performer',    score: `${topPerformer}/100`, color: '#16a34a', border: '#16a34a' },
              ].filter(Boolean).map((item, i, arr) => (
                <div 
                  key={i} 
                  style={{ 
                    padding: isMobile ? '16px 16px' : (isTablet ? '18px 20px' : '20px 24px'), 
                    textAlign: 'center', 
                    borderRight: isMobile 
                      ? 'none' 
                      : (i < arr.length - 1 ? '0.5px solid rgba(0,0,0,0.06)' : 'none'),
                    borderBottom: isMobile && i < arr.length - 1 
                      ? '0.5px solid rgba(0,0,0,0.06)' 
                      : 'none',
                    borderTop: `3px solid ${item.border}`,
                  }}
                >
                  <p style={{ 
                    fontFamily: 'inherit', 
                    fontSize: isMobile ? 10 : 11, 
                    fontWeight: 600, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.06em', 
                    color: '#94a3b8', 
                    margin: isMobile ? '0 0 6px' : '0 0 8px' 
                  }}>
                    {item.label}
                  </p>
                  <p style={{ 
                    fontFamily: "'Sansation', sans-serif", 
                    fontSize: isMobile ? 22 : (isTablet ? 24 : 26), 
                    fontWeight: 800, 
                    color: item.color, 
                    margin: 0, 
                    letterSpacing: '-0.02em' 
                  }}>
                    {item.score}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ 
            background: '#ffffff', 
            borderRadius: isMobile ? 14 : 20, 
            padding: isMobile ? 20 : 28, 
            border: '0.5px solid rgba(0,0,0,0.06)' 
          }}>
            <EmptyState icon="Target" title="No benchmark data" desc="Radar benchmark will populate from your HTML report." />
          </div>
        )
      )}

      {/* ── TABLE VIEW ── */}
      {benchView === 'table' && (
        competitorData.length > 0 ? (
          <div style={{ 
            background: '#ffffff', 
            border: '0.5px solid rgba(0,0,0,0.06)', 
            borderRadius: isMobile ? 14 : (isTablet ? 16 : 20), 
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            <div style={{ 
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: isMobile ? '600px' : 'auto',
              }}>
                <thead>
                  <tr style={{ 
                    background: '#f8fafc', 
                    borderBottom: '2px solid #e2e8f0' 
                  }}>
                    {['Company', 'Overall', 'Narrative', 'Design', 'Data', 'Structure', 'Persuasion'].map(h => (
                      <th 
                        key={h} 
                        style={{ 
                          textAlign: 'left', 
                          padding: isMobile ? '10px 12px' : '12px 16px', 
                          fontFamily: 'inherit', 
                          fontSize: isMobile ? 9 : 10, 
                          fontWeight: 600, 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.08em', 
                          color: '#94a3b8',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {competitorData.map((row, i) => {
                    const score = parseInt(row.overall) || 0;
                    const sc = score >= 80 ? '#16a34a' : score >= 65 ? '#d97706' : '#dc2626';
                    return (
                      <tr
                        key={i}
                        style={{ 
                          borderBottom: '1px solid #f1f5f9', 
                          background: row.isYou ? 'rgba(0,74,173,0.05)' : 'transparent', 
                          transition: 'background 0.1s' 
                        }}
                        onMouseEnter={e => { if (!row.isYou) e.currentTarget.style.background = '#fafbfc'; }}
                        onMouseLeave={e => { if (!row.isYou) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ 
                          padding: isMobile ? '12px 12px' : '14px 16px',
                          minWidth: isMobile ? '140px' : 'auto',
                        }}>
                          <span style={{ 
                            fontFamily: "'Sansation', sans-serif", 
                            fontWeight: 700, 
                            fontSize: isMobile ? 12 : 13, 
                            color: row.isYou ? BLUE : '#0f172a',
                            wordBreak: 'break-word',
                          }}>
                            {row.name}
                          </span>
                          {row.isYou && (
                            <span style={{ 
                              fontFamily: 'inherit', 
                              fontSize: isMobile ? 9 : 10, 
                              fontWeight: 700, 
                              padding: '2px 6px', 
                              borderRadius: 20, 
                              marginLeft: 6, 
                              background: GRAD_ACTIVE, 
                              color: '#fff',
                              display: 'inline-block',
                            }}>
                              You
                            </span>
                          )}
                        </td>
                        <td style={{ 
                          padding: isMobile ? '12px 12px' : '14px 16px' 
                        }}>
                          <span style={{ 
                            fontFamily: "'Sansation', sans-serif", 
                            fontWeight: 700, 
                            fontSize: isMobile ? 12 : 13, 
                            color: sc 
                          }}>
                            {row.overall}
                          </span>
                        </td>
                        {[row.narrative, row.design, row.data, row.structure, row.persuasion].map((v, vi) => (
                          <td 
                            key={vi} 
                            style={{ 
                              padding: isMobile ? '12px 12px' : '14px 16px', 
                              fontFamily: 'inherit', 
                              fontSize: isMobile ? 12 : 13, 
                              fontWeight: 400, 
                              color: '#64748b' 
                            }}
                          >
                            {v || '—'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ 
            background: '#ffffff', 
            borderRadius: isMobile ? 14 : 20, 
            padding: isMobile ? 20 : 28, 
            border: '0.5px solid rgba(0,0,0,0.06)' 
          }}>
            <EmptyState icon="Table2" title="No competitor data" desc="Competitor comparison table will populate from your HTML report." />
          </div>
        )
      )}
    </div>
  );
};

export default CompetitorBenchmark;